/**
 * Scientific content for the illustrative Caprelsa® (vandetanib) Snak.
 * Foundation: Caprelsa EU SmPC (EMA EPAR) and the pivotal ZETA trial (Wells SA Jr et al. J Clin Oncol 2012;30(2):134–141).
 * All copy requires ESTEVE medical/legal/regulatory review before any HCP use.
 */

export const EMA_EPAR_URL = 'https://www.ema.europa.eu/en/medicines/human/EPAR/caprelsa'
export const ZETA_REFERENCE = 'Wells SA Jr, et al. J Clin Oncol. 2012;30(2):134–141.'

export const conceptDisclaimer =
  'Illustrative, unapproved concept · For discussion only · Requires ESTEVE medical, legal & regulatory review before HCP use.'

/** Fictional patient used for the Interact scene. */
export const fictionalPatient = {
  name: 'Patient A.',
  age: 54,
  summary: 'Unresectable, metastatic MTC',
  tags: ['Rising calcitonin & CEA', 'Diarrhoea', 'Fatigue'],
}

export type ConsiderationId = 'progression' | 'ret' | 'safety'

export const considerations: { id: ConsiderationId; label: string; short: string; title: string; body: string }[] = [
  {
    id: 'progression',
    label: 'Disease progression & symptoms',
    short: 'Progression',
    title: 'Is the disease aggressive and symptomatic?',
    body: 'Progressive, symptomatic disease is where systemic treatment is considered. Short calcitonin and CEA doubling times can signal a faster pace.',
  },
  {
    id: 'ret',
    label: 'RET mutation status',
    short: 'RET status',
    title: 'Is RET mutation status known?',
    body: 'RET status informs treatment selection. Check the current EU SmPC for the approved indication wording.',
  },
  {
    id: 'safety',
    label: 'Benefit–risk & safety',
    short: 'Safety',
    title: 'Is the benefit–risk profile appropriate?',
    body: 'Vandetanib can prolong the QTc interval. ECG, electrolytes and interacting medicines must be assessed before and during treatment.',
  },
]

/** Pivotal ZETA trial — progression-free survival (primary endpoint). */
export const zetaPfs = {
  caprelsa: 30.5,
  placebo: 19.3,
  axisMax: 36,
  hazardRatio: '0.46',
  ci: '95% CI 0.31–0.69',
  p: 'p<0.001',
  n: 331,
  endpoint: 'Primary endpoint: progression-free survival (PFS)',
  population: 'Adults with unresectable locally advanced or metastatic MTC',
  design: 'Phase III · randomized 2:1 · double-blind · placebo-controlled',
  notes: [
    'Vandetanib median PFS is a predicted value (Weibull model); median not reached at data cut-off. Placebo median 19.3 months observed.',
    'Patients on placebo could switch to open-label vandetanib on progression. No overall survival benefit was demonstrated at the time of analysis.',
    'Historical placebo-controlled results — not a comparison with current active treatments.',
  ],
}

export type ReceptorId = 'ret' | 'vegfr' | 'egfr'

export const receptors: { id: ReceptorId; label: string; effect: string }[] = [
  { id: 'ret', label: 'RET', effect: 'Tumour-cell proliferation' },
  { id: 'vegfr', label: 'VEGFR', effect: 'Tumour angiogenesis' },
  { id: 'egfr', label: 'EGFR', effect: 'Tumour-cell survival' },
]

export const keySafety = [
  'QTc prolongation, Torsades de pointes and sudden death have been reported.',
  'Contraindicated in congenital long QTc syndrome, QTc >480 ms, and with medicines known to prolong QTc (see SmPC).',
  'ECG and serum potassium, calcium, magnesium and TSH before and during treatment.',
  'Other important risks include diarrhoea, rash/photosensitivity, hypertension and PRES.',
]

export const evidenceSummary = [
  { value: '331', label: 'patients randomized' },
  { value: 'HR 0.46', label: 'PFS · 95% CI 0.31–0.69' },
  { value: 'p<0.001', label: 'primary endpoint met' },
]
